import React, { useEffect, useMemo, useState } from "react";
import {
  Card,
  Form,
  Row,
  Col,
  Input,
  DatePicker,
  Select,
  Button,
  Typography,
  Space,
  message,
  Spin,
  Modal,
} from "antd";
import { DeleteOutlined, EditOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useParams } from "react-router-dom";
import axiosInstance from "../../../../configs";
import { canEdit, MODULES } from "../../../../utils/rbac";

const { Text } = Typography;


const DOC_OPTIONS = [
  { label: "BC 1.6", value: 16 },
  { label: "BC 2.7", value: 27 },
  { label: "BC 4.0", value: 40 },
];

export default function FormEditPemasukanBarang() {
  const [form] = Form.useForm();
  const { idIncoming } = useParams(); // pastikan route: /inv/incoming/edit/:idIncoming

  const [pageLoading, setPageLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  const [headerData, setHeaderData] = useState(null); // header asli dari BE
  const [originalDetails, setOriginalDetails] = useState([]); // details asli (untuk deleted_detail_ids)

  // Confirmation Modal State
  const [confirmVisible, setConfirmVisible] = useState(false);
  const [pendingValues, setPendingValues] = useState(null);

  // watch barang untuk total kemasan
  const barangWatch = Form.useWatch("barang", form);
  const totalKemasan = useMemo(() => {
    if (!Array.isArray(barangWatch)) return 0;
    return barangWatch.reduce(
      (sum, row) => sum + (Number(row?.qtyKemasan) || 0),
      0
    );
  }, [barangWatch]);

  // ----------- LOAD DATA AWAL (VIEW MODE) -------------
  useEffect(() => {
    const fetchData = async () => {
      setPageLoading(true);
      try {
        const res = await axiosInstance.get(
          `/incoming/${idIncoming}/with-details`
        );

        const header = res.data?.header || {};
        const details = res.data?.details || [];

        setHeaderData(header);
        setOriginalDetails(details);

        // mapping header -> form
        form.setFieldsValue({
          bpb_number: header.bpb_number,
          bpb_date: header.bpb_date ? dayjs(header.bpb_date) : null,
          id_document: header.id_document || undefined,
          nopen_number: header.document_number,
          document_date: header.document_date
            ? dayjs(header.document_date)
            : null,
          supplier_name: header.id_contact || "",
          owner_name: header.id_customer || "",
          bl_number: header.purchase_number || "",
          bl_date: header.bl_date ? dayjs(header.bl_date) : null,
          barang: details.map((d) => ({
            id_incoming_detail: d.id_incoming_detail,
            namaBarang: d.item_code, // kamu bisa ganti jadi item master lookup kalau mau
            seri: d.seri,
            qtyKemasan: d.total_kemasan,
            kemasan: d.kemasan,
            qtySatuan: d.total_satuan,
            satuan: d.satuan,
          })),
        });
      } catch (err) {
        console.error(err);
        message.error("Gagal mengambil data pemasukan.");
      } finally {
        setPageLoading(false);
      }
    };

    if (idIncoming) fetchData();
  }, [idIncoming, form]);

  // helper: map originalDetails by id untuk base payload
  const originalDetailMap = useMemo(() => {
    const m = new Map();
    originalDetails.forEach((d) => {
      m.set(d.id_incoming_detail, d);
    });
    return m;
  }, [originalDetails]);

  const handleCancel = () => {
    window.history.back();
  };

  // ----------- SUBMIT (UPDATE) -------------
  const onFinish = async (values) => {
    if (!headerData) {
      message.error("Data header tidak tersedia.");
      return;
    }
    setPendingValues(values);
    setConfirmVisible(true);
  };

  const handleConfirmSubmit = async () => {
    if (!headerData) {
      message.error("Data header tidak tersedia.");
      return;
    }

    try {
      setSaving(true);
      const values = pendingValues;

      // --- header payload: ambil dari header lama + override field editable
      // Exclude nama_customer dan created_at agar tidak ikut terkirim/updated
      const { nama_customer, created_at, ...restHeader } = headerData;

      const headerPayload = {
        ...restHeader,
        id_document: values.id_document ?? headerData.id_document,
        bpb_number: values.bpb_number || headerData.bpb_number,
        bpb_date: values.bpb_date
          ? dayjs(values.bpb_date).format("YYYY-MM-DD")
          : headerData.bpb_date,
        document_number: values.nopen_number || headerData.document_number,
        document_date: values.document_date
          ? dayjs(values.document_date).format("YYYY-MM-DD")
          : headerData.document_date,
        purchase_number: values.bl_number || headerData.purchase_number,
        bl_date: values.bl_date
          ? dayjs(values.bl_date).format("YYYY-MM-DD")
          : headerData.bl_date,
        total_packaging: totalKemasan || headerData.total_packaging || 0,
        updated_by: 0, // sesuaikan kalau punya user id
        updated_at: dayjs().format("YYYY-MM-DD HH:mm:ss"),
      };

      // --- details payload: gabungan data lama + perubahan dari form
      const barangForm = values.barang || [];

      const detailsPayload = barangForm.map((row) => {
        const idDetail = row.id_incoming_detail;
        const original = idDetail ? originalDetailMap.get(idDetail) : null;

        const base = original || {
          id_incoming_detail: 0,
          id_incoming: headerData.id_incoming,
          nopen: headerData.document_number,
          tanggal_nopen: headerData.document_date || null,
          merk: null,
          id_currency: headerData.id_cif_currency || null,
          satuan: row.satuan,
          total_satuan: 0,
          sub_satuan: null,
          quantity: null,
          total_price: null,
          kemasan: row.kemasan,
          total_kemasan: 0,
          created_by: 0,
          updated_by: 0,
        };

        // Exclude created_at from details to prevent date format errors
        const { created_at, ...restBase } = base;

        return {
          ...restBase,
          // override field yang memang diedit di FE:
          seri: Number(row.seri) || 0,
          item_code: row.namaBarang,
          satuan: row.satuan,
          total_satuan: Number(row.qtySatuan) || 0,
          kemasan: row.kemasan,
          total_kemasan: Number(row.qtyKemasan) || 0,
          updated_by: 0,
        };
      });

      // --- deleted_detail_ids
      const currentIds = new Set(
        barangForm
          .map((row) => row.id_incoming_detail)
          .filter((id) => id !== undefined && id !== null)
      );

      const deletedDetailIds = originalDetails
        .map((d) => d.id_incoming_detail)
        .filter((id) => !currentIds.has(id));

      const payload = {
        header: headerPayload,
        details: detailsPayload,
        deleted_detail_ids: deletedDetailIds,
      };

      await axiosInstance.put(
        `/incoming/${idIncoming}/with-details`,
        payload
      );

      message.success("Pemasukan barang berhasil diupdate.");
      setConfirmVisible(false);
      // Redirect to incoming list
      window.location.href = "/#/inventory/incoming";
    } catch (err) {
      console.error(err);
      message.error("Gagal mengupdate pemasukan barang.");
      setPageLoading(false);
    } finally {
      setSaving(false);
    }
  };

  // ----------- RENDER -------------
  const handleCancelEdit = () => {
    // reset form ke nilai terakhir headerData/originalDetails
    if (!headerData) return;
    form.resetFields();
    form.setFieldsValue({
      bpb_number: headerData.bpb_number,
      bpb_date: headerData.bpb_date ? dayjs(headerData.bpb_date) : null,
      id_document: headerData.id_document || undefined,
      nopen_number: headerData.document_number,
      document_date: headerData.document_date
        ? dayjs(headerData.document_date)
        : null,
      supplier_name: headerData.nama_pemasok || "",
      owner_name: headerData.id_contact || "",
      bl_number: headerData.purchase_number || "",
      bl_date: headerData.bl_date ? dayjs(headerData.bl_date) : null,
      barang: originalDetails.map((d) => ({
        id_incoming_detail: d.id_incoming_detail,
        namaBarang: d.item_code,
        seri: d.seri,
        qtyKemasan: d.total_kemasan,
        kemasan: d.kemasan,
        qtySatuan: d.total_satuan,
        satuan: d.satuan,
      })),
    });
    setIsEditing(false);
  };

  return (
    <Card className="kt-portlet" bodyStyle={{ padding: 24 }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Typography.Title level={3} style={{ marginBottom: 4 }}>
            Detail Pemasukan Barang
          </Typography.Title>
          <Text type="secondary">
            Lihat dan perbarui data pemasukan barang beserta detail barangnya.
          </Text>
        </Col>
        <Col>
          <Space>
            {!isEditing && canEdit(MODULES.INVENTORY) && (
              <Button
                icon={<EditOutlined />}
                onClick={() => setIsEditing(true)}
                htmlType="button" // PENTING: bukan submit
              >
                Edit
              </Button>
            )}
            <Button onClick={handleCancel}>Kembali</Button>
          </Space>
        </Col>
      </Row>

      <Spin spinning={pageLoading}>
        <Form
          id="form-edit-pemasukan"
          form={form}
          layout="vertical"
          onFinish={onFinish}
          disabled={!isEditing} // view-only ketika !isEditing
        >
          {/* BARIS: BPB */}
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                label={<Text strong>No BPB</Text>}
                name="bpb_number"
                rules={[{ required: true, message: "No BPB wajib diisi" }]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                label={<Text strong>Tanggal BPB</Text>}
                name="bpb_date"
                rules={[{ required: true, message: "Tanggal BPB wajib diisi" }]}
              >
                <DatePicker style={{ width: "100%" }} format="DD MMMM YYYY" />
              </Form.Item>
            </Col>
          </Row>

          {/* BARIS: Dok Pabean */}
          <Row gutter={16}>
            <Col xs={24} md={8}>
              <Form.Item
                label={<Text strong>Jenis Dokumen Pabean</Text>}
                name="id_document"
                rules={[
                  { required: true, message: "Jenis dokumen wajib dipilih" },
                ]}
              >
                <Select
                  placeholder="Pilih Jenis Dokumen"
                  options={DOC_OPTIONS}
                />
              </Form.Item>
            </Col>

            <Col xs={24} md={8}>
              <Form.Item
                label={<Text strong>No Dokumen Pabean</Text>}
                name="nopen_number"
                rules={[{ required: true, message: "No dokumen wajib diisi" }]}
              >
                <Input placeholder="No Dokumen Pabean" />
              </Form.Item>
            </Col>

            <Col xs={24} md={8}>
              <Form.Item
                label={<Text strong>Tanggal Dokumen Pabean</Text>}
                name="document_date"
              >
                <DatePicker style={{ width: "100%" }} format="DD MMM YYYY" />
              </Form.Item>
            </Col>
          </Row>

          {/* BARIS: Pemasok & Pemilik */}
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                label={<Text strong>Pemasok / Pengirim</Text>}
                name="supplier_name"
              >
                <Input placeholder="Pemasok / Pengirim" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                label={<Text strong>Pemilik Barang</Text>}
                name="owner_name"
              >
                <Input placeholder="Pemilik Barang" />
              </Form.Item>
            </Col>
          </Row>

          {/* BARIS: BL */}
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item label={<Text strong>Nomor BL</Text>} name="bl_number">
                <Input placeholder="Nomor BL" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label={<Text strong>Tanggal BL</Text>} name="bl_date">
                <DatePicker style={{ width: "100%" }} format="DD MMM YYYY" />
              </Form.Item>
            </Col>
          </Row>

          {/* TABEL BARANG */}
          <Card
            bodyStyle={{ padding: 0 }}
            style={{
              marginTop: 8,
              borderRadius: 2,
              border: "1px solid #E4E6EF",
              overflow: "hidden",
            }}
          >
            {/* Header */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "3fr 0.8fr 1.4fr 1.2fr 1.4fr 1.1fr 60px",
                background: "#F3F6F9",
                padding: "8px 16px",
                fontWeight: 600,
                fontSize: 13,
              }}
            >
              <div>NAMA BARANG *</div>
              <div>SERI</div>
              <div>QTY KEMASAN</div>
              <div>KEMASAN</div>
              <div>QTY SATUAN</div>
              <div>SATUAN</div>
              <div style={{ textAlign: "center" }}>AKSI</div>
            </div>

            <Form.List name="barang">
              {(fields, { remove, add }) => (
                <>
                  {fields.map((field) => (
                    <div
                      key={field.key}
                      style={{
                        display: "grid",
                        gridTemplateColumns:
                          "3fr 0.8fr 1.4fr 1.2fr 1.4fr 1.1fr 60px",
                        padding: "8px 16px",
                        borderTop: "1px solid #E4E6EF",
                        alignItems: "center",
                      }}
                    >
                      {/* hidden id detail utk payload */}
                      <Form.Item
                        {...field}
                        name={[field.name, "id_incoming_detail"]}
                        fieldKey={[field.fieldKey, "id_incoming_detail"]}
                        style={{ display: "none" }}
                      >
                        <Input type="hidden" />
                      </Form.Item>

                      {/* Nama Barang */}
                      <Form.Item
                        {...field}
                        name={[field.name, "namaBarang"]}
                        fieldKey={[field.fieldKey, "namaBarang"]}
                        style={{ marginBottom: 0, paddingRight: 8 }}
                        rules={[
                          {
                            required: true,
                            message: "Nama barang wajib diisi",
                          },
                        ]}
                      >
                        <Input placeholder="Nama Barang" />
                      </Form.Item>

                      {/* Seri */}
                      <Form.Item
                        {...field}
                        name={[field.name, "seri"]}
                        fieldKey={[field.fieldKey, "seri"]}
                        style={{ marginBottom: 0, paddingRight: 8 }}
                      >
                        <Input placeholder="Seri" />
                      </Form.Item>

                      {/* Qty Kemasan */}
                      <Form.Item
                        {...field}
                        name={[field.name, "qtyKemasan"]}
                        fieldKey={[field.fieldKey, "qtyKemasan"]}
                        style={{ marginBottom: 0, paddingRight: 8 }}
                      >
                        <Input type="number" placeholder="Qty Kemasan" />
                      </Form.Item>

                      {/* Kemasan */}
                      <Form.Item
                        {...field}
                        name={[field.name, "kemasan"]}
                        fieldKey={[field.fieldKey, "kemasan"]}
                        style={{ marginBottom: 0, paddingRight: 8 }}
                      >
                        <Input placeholder="Kemasan" />
                      </Form.Item>

                      {/* Qty Satuan */}
                      <Form.Item
                        {...field}
                        name={[field.name, "qtySatuan"]}
                        fieldKey={[field.fieldKey, "qtySatuan"]}
                        style={{ marginBottom: 0, paddingRight: 8 }}
                      >
                        <Input type="number" placeholder="Qty Satuan" />
                      </Form.Item>

                      {/* Satuan */}
                      <Form.Item
                        {...field}
                        name={[field.name, "satuan"]}
                        fieldKey={[field.fieldKey, "satuan"]}
                        style={{ marginBottom: 0, paddingRight: 8 }}
                      >
                        <Input placeholder="Satuan" />
                      </Form.Item>

                      <div style={{ textAlign: "center" }}>
                        <Button
                          type="text"
                          icon={<DeleteOutlined style={{ color: "#F64E60" }} />}
                          onClick={() => remove(field.name)}
                          disabled={!isEditing}
                        />
                      </div>
                    </div>
                  ))}

                  {fields.length === 0 && (
                    <div
                      style={{
                        padding: "12px 16px",
                        borderTop: "1px solid #E4E6EF",
                        color: "#B5B5C3",
                        fontStyle: "italic",
                      }}
                    >
                      Belum ada data barang.
                    </div>
                  )}

                  {isEditing && (
                    <div
                      style={{
                        padding: "8px 16px 12px",
                        borderTop: "1px solid #E4E6EF",
                      }}
                    >
                      <Button
                        type="dashed"
                        onClick={() =>
                          add({
                            id_incoming_detail: undefined,
                            namaBarang: "",
                            seri: "",
                            qtyKemasan: 0,
                            kemasan: "",
                            qtySatuan: 0,
                            satuan: "",
                          })
                        }
                      >
                        Tambah Barang
                      </Button>
                    </div>
                  )}
                </>
              )}
            </Form.List>
          </Card>

          {/* TOTAL KEMASAN */}
          <div
            style={{
              marginTop: 12,
              textAlign: "right",
              fontWeight: 600,
            }}
          >
            Total Kemasan: {totalKemasan}
          </div>

          <Row justify="end" style={{ marginTop: 16 }}>
            {isEditing && (
              <Space>
                <Button onClick={handleCancelEdit}>Batal</Button>

                <Button
                  type="primary"
                  htmlType="submit" // HANYA INI yang submit
                  loading={saving}
                  style={{ minWidth: 140, fontWeight: 600 }}
                >
                  Simpan Perubahan
                </Button>
              </Space>
            )}
          </Row>
        </Form>
      </Spin>

      <Modal
        title="Konfirmasi Update"
        open={confirmVisible}
        onOk={handleConfirmSubmit}
        onCancel={() => setConfirmVisible(false)}
        okText="Ya, Update"
        cancelText="Batal"
        confirmLoading={saving}
      >
        <p>Apakah Anda yakin ingin menyimpan perubahan data pemasukan ini?</p>
        <div style={{ background: "#f5f5f5", padding: 10, borderRadius: 4 }}>
          <p style={{ margin: 0 }}>
            <strong>No BPB:</strong> {pendingValues?.bpb_number}
          </p>
          <p style={{ margin: 0 }}>
            <strong>Tanggal BPB:</strong>{" "}
            {pendingValues?.bpb_date
              ? dayjs(pendingValues.bpb_date).format("DD MMM YYYY")
              : "-"}
          </p>
          <p style={{ margin: 0 }}>
            <strong>No Dokumen:</strong> {pendingValues?.nopen_number}
          </p>
        </div>

        <div style={{ marginTop: 16 }}>
          <Text strong>Detail Barang:</Text>
          <div style={{ maxHeight: 200, overflowY: "auto", marginTop: 8, border: "1px solid #f0f0f0" }}>
            <table style={{ width: "100%", fontSize: 12, borderCollapse: "collapse" }}>
              <thead style={{ background: "#fafafa", position: "sticky", top: 0 }}>
                <tr>
                  <th style={{ padding: "4px 8px", textAlign: "left", borderBottom: "1px solid #f0f0f0" }}>Nama Barang</th>
                  <th style={{ padding: "4px 8px", textAlign: "right", borderBottom: "1px solid #f0f0f0" }}>Jml Kemasan</th>
                  <th style={{ padding: "4px 8px", textAlign: "left", borderBottom: "1px solid #f0f0f0" }}>Kemasan</th>
                </tr>
              </thead>
              <tbody>
                {pendingValues?.barang?.map((item, idx) => (
                  <tr key={idx} style={{ borderBottom: "1px solid #f0f0f0" }}>
                    <td style={{ padding: "4px 8px" }}>{item.namaBarang}</td>
                    <td style={{ padding: "4px 8px", textAlign: "right" }}>{item.qtyKemasan}</td>
                    <td style={{ padding: "4px 8px" }}>{item.kemasan}</td>
                  </tr>
                ))}
                {(!pendingValues?.barang || pendingValues.barang.length === 0) && (
                  <tr>
                    <td colSpan={3} style={{ padding: "8px", textAlign: "center", color: "#999" }}>
                      Tidak ada barang
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </Modal>
    </Card>
  );
}
